import { create } from 'zustand';
import customFetch from '@/lib/utils/fetch';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useSWR from 'swr';
import useSWRInfinite, { type SWRInfiniteConfiguration } from 'swr/infinite';
import useSWRMutation from 'swr/mutation';
// 仅类型导入（编译期擦除），不会把服务端 marketDb 代码打进客户端 bundle
import type { PolymarketEventRow, PolymarketMarketRow } from '@/lib/marketDb/market';

export type FollowType = 'market' | 'entity';

interface FollowingStore {
  followingIds: Set<string>;
  followStatusById: Map<string, boolean>;
  checkedIds: Set<string>;
  setFollowed: (key: string, followed: boolean) => void;
  setFollowedBatch: (
    items: { followedId: string; type: FollowType; isFollowed: boolean }[],
  ) => void;
}

function makeKey(type: FollowType, id: string) {
  return `${type}:${id}`;
}

export const useFollowingStore = create<FollowingStore>((set) => ({
  followingIds: new Set(),
  followStatusById: new Map(),
  checkedIds: new Set(),
  setFollowed: (key, followed) =>
    set((state) => {
      const next = new Set(state.followingIds);
      const nextStatus = new Map(state.followStatusById);
      const nextChecked = new Set(state.checkedIds);
      followed ? next.add(key) : next.delete(key);
      nextStatus.set(key, followed);
      nextChecked.add(key);
      return { followingIds: next, followStatusById: nextStatus, checkedIds: nextChecked };
    }),
  setFollowedBatch: (items) =>
    set((state) => {
      const next = new Set(state.followingIds);
      const nextStatus = new Map(state.followStatusById);
      const nextChecked = new Set(state.checkedIds);

      items.forEach(({ followedId, type, isFollowed }) => {
        const key = makeKey(type, followedId);
        isFollowed ? next.add(key) : next.delete(key);
        nextStatus.set(key, isFollowed);
        nextChecked.add(key);
      });

      return { followingIds: next, followStatusById: nextStatus, checkedIds: nextChecked };
    }),
}));

// ── Batch check mechanism (grouped by type) ─────────────────────────────────

const FOLLOW_CHECK_BATCH_DELAY_MS = 20;

type PendingResolvers = Array<{ resolve: () => void; reject: (error: unknown) => void }>;

const pendingByType = new Map<FollowType, Set<string>>();
const resolversByType = new Map<FollowType, PendingResolvers>();
let followCheckTimer: ReturnType<typeof setTimeout> | null = null;

function flushFollowCheckBatch() {
  if (followCheckTimer) {
    clearTimeout(followCheckTimer);
    followCheckTimer = null;
  }

  const snapshots = new Map<FollowType, { ids: string[]; resolvers: PendingResolvers }>();
  for (const type of ['market', 'entity'] as FollowType[]) {
    const ids = [...(pendingByType.get(type) ?? [])];
    const resolvers = resolversByType.get(type) ?? [];
    if (ids.length > 0) snapshots.set(type, { ids, resolvers });
    pendingByType.delete(type);
    resolversByType.delete(type);
  }

  for (const [type, { ids, resolvers }] of snapshots) {
    customFetch('/api/market/follow/check', {
      method: 'POST',
      body: JSON.stringify({ followedIds: ids, type }),
    })
      .then(async (resp) => {
        const res = await resp.json();
        useFollowingStore.getState().setFollowedBatch(res.data?.follows || []);
        resolvers.forEach(({ resolve }) => resolve());
      })
      .catch((error) => {
        console.log(error);
        resolvers.forEach(({ reject }) => reject(error));
      });
  }
}

function normalizeIds(ids: string[]) {
  return [...new Set(ids.map(String).filter(Boolean))];
}

function enqueueFollowCheck(ids: string[], type: FollowType) {
  const normalizedIds = normalizeIds(ids);
  const { checkedIds } = useFollowingStore.getState();
  const uncheckedIds = normalizedIds.filter((id) => !checkedIds.has(makeKey(type, id)));

  if (!uncheckedIds.length) return Promise.resolve();

  if (!pendingByType.has(type)) pendingByType.set(type, new Set());
  if (!resolversByType.has(type)) resolversByType.set(type, []);

  uncheckedIds.forEach((id) => pendingByType.get(type)!.add(id));

  const promise = new Promise<void>((resolve, reject) => {
    resolversByType.get(type)!.push({ resolve, reject });
  });

  if (!followCheckTimer) {
    followCheckTimer = setTimeout(flushFollowCheckBatch, FOLLOW_CHECK_BATCH_DELAY_MS);
  }

  return promise;
}

// ── 关注列表（GET /api/market/follow，滚动加载） ─────────────────────────────

export interface FollowingEntity {
  id: number;
  entity_type: string;
  entity_name: string;
  source_category: string | null;
  logo: string | null;
  volume: number;
  market_count: number;
  created_at?: string;
  updated_at?: string;
}

// GET /api/market/follow 的 market 字段来自 getPolymarketEventsWithTopMarkets
// （event 含 markets 全量行，外层 markets 为同一数组，marketLimit=3）
export interface FollowingItem {
  id: number;
  userId: number;
  followedId: number;
  type: FollowType;
  createdAt: string;
  updatedAt: string;
  market?: {
    event: PolymarketEventRow;
    markets: PolymarketMarketRow[];
  } | null;
  entity?: FollowingEntity | null;
}

export interface FollowingPagination {
  page: number;
  limit: number;
  total: number;
  total_pages: number;
}

export interface FollowingListResponse {
  success: boolean;
  data: {
    list: FollowingItem[];
    pagination: FollowingPagination;
  };
}

export interface FetchFollowingListParams {
  limit?: number;
  offset?: number;
  /** 按类型筛选；缺省查全部 */
  type?: FollowType;
}

export interface UseFollowingListOptions {
  /** 每页条数（1-100），默认 20 */
  pageSize?: number;
  /** 按类型筛选；缺省查全部 */
  type?: FollowType;
  enabled?: boolean;
  swr?: SWRInfiniteConfiguration<FollowingListResponse, Error>;
}

const FOLLOWING_LIST_PAGE_SIZE = 20;

export async function fetchFollowingList(params: FetchFollowingListParams = {}) {
  const { limit = FOLLOWING_LIST_PAGE_SIZE, offset = 0, type } = params;

  const searchParams = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
  });
  if (type) searchParams.set('type', type);

  const response = await customFetch(`/api/market/follow?${searchParams.toString()}`);

  if (!(response instanceof Response)) {
    throw response instanceof Error ? response : new Error('Failed to fetch following list');
  }

  const result = (await response.json()) as FollowingListResponse;

  if (!result.success) {
    throw new Error('Failed to fetch following list');
  }

  return result;
}

/**
 * 关注列表（limit/offset 分页滚动加载，useSWRInfinite）。
 * 返回扁平化的 list，`loadMore` / `setSentinelRef` 触发加载下一页。
 */
export function useFollowingList(options: UseFollowingListOptions = {}) {
  const { pageSize = FOLLOWING_LIST_PAGE_SIZE, type, enabled = true, swr: swrOptions } = options;

  const getKey = useCallback(
    (pageIndex: number, previousPageData: FollowingListResponse | null) => {
      if (!enabled) return null;
      // 上一页不满一页时视为最后一页
      if (previousPageData && previousPageData.data.list.length < pageSize) return null;

      return ['following-list', pageSize, pageIndex, type ?? ''] as const;
    },
    [enabled, pageSize, type],
  );

  const { data, error, isLoading, isValidating, size, setSize, mutate } = useSWRInfinite<
    FollowingListResponse,
    Error
  >(
    getKey,
    ([, limit, pageIndex]: readonly [string, number, number, string]) =>
      fetchFollowingList({ limit, offset: pageIndex * limit, type }),
    {
      revalidateOnFocus: false,
      revalidateFirstPage: false,
      ...swrOptions,
    },
  );

  // memo 保持引用稳定：data 未变化时 list 不产生新数组，避免下游 useEffect/useMemo 反复触发
  const list = useMemo(() => data?.flatMap((page) => page.data.list) ?? [], [data]);
  const total = data?.[0]?.data.pagination.total ?? 0;
  const lastPage = data?.[data.length - 1];
  const hasMore = lastPage ? lastPage.data.list.length >= pageSize : false;

  const loadMore = useCallback(() => {
    if (!isValidating && hasMore) {
      setSize((s) => s + 1);
    }
  }, [isValidating, hasMore, setSize]);

  // IntersectionObserver sentinel — 绑定到列表底部元素，进入视口自动加载下一页
  const sentinelRef = useRef<Element | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const setSentinelRef = useCallback(
    (el: Element | null) => {
      observerRef.current?.disconnect();
      observerRef.current = null;
      sentinelRef.current = el;

      if (!el) return;

      observerRef.current = new IntersectionObserver(
        (entries) => {
          if (entries[0]?.isIntersecting) loadMore();
        },
        { threshold: 0.1 },
      );
      observerRef.current.observe(el);
    },
    [loadMore],
  );

  useEffect(() => {
    return () => {
      observerRef.current?.disconnect();
    };
  }, []);

  return {
    list,
    total,
    hasMore,
    isLoading,
    isValidating,
    error,
    page: size,
    loadMore,
    setSentinelRef,
    refresh: mutate,
  };
}

// ── Hook ─────────────────────────────────────────────────────────────────────

export function useFollowing(
  followedId?: string | null,
  followed?: boolean,
  type: FollowType = 'market',
) {
  const { data: apiAccessToken } = useSWR('apiAccessToken');
  const { trigger: setLoginDialogOpen } = useSWRMutation('loginDialogOpen');
  const resolvedId = `${followedId || ''}`;
  const storeKey = resolvedId ? makeKey(type, resolvedId) : '';
  const { followStatusById, setFollowed } = useFollowingStore();
  const [isFollowing, setFollowing] = useState(false);

  const storedFollowStatus = storeKey ? followStatusById.get(storeKey) : undefined;
  const isFollowed =
    typeof storedFollowStatus === 'boolean' ? storedFollowStatus : followed === true;

  const toggleFollow = useCallback(async () => {
    if (!followedId || isFollowing) return;

    if (!apiAccessToken) {
      setLoginDialogOpen(true);
      return;
    }

    setFollowing(true);
    try {
      const method = isFollowed ? 'DELETE' : 'POST';
      const response = await customFetch('/api/market/follow', {
        method,
        body: JSON.stringify({ followedId: resolvedId, type }),
      });

      if (!response || response.status === 401) {
        setLoginDialogOpen(true);
        return;
      }

      setFollowed(storeKey, !isFollowed);
    } catch (err) {
      console.error('Error toggling follow:', err);
    } finally {
      setFollowing(false);
    }
  }, [
    apiAccessToken,
    followedId,
    isFollowed,
    isFollowing,
    resolvedId,
    setFollowed,
    setLoginDialogOpen,
    storeKey,
    type,
  ]);

  const checkIfFollowed = useCallback(
    async (ids: string[]) => {
      if (!apiAccessToken) return;
      try {
        await enqueueFollowCheck(ids, type);
      } catch (err) {
        console.log(err);
      }
    },
    [apiAccessToken, type],
  );

  useEffect(() => {
    if (!apiAccessToken || !followedId) return;
    if (!useFollowingStore.getState().checkedIds.has(storeKey)) {
      checkIfFollowed([followedId]);
    }
  }, [apiAccessToken, checkIfFollowed, followedId, storeKey]);

  return {
    isFollowed,
    isFollowing,
    toggleFollow,
    setFollowed: (f: boolean) => setFollowed(storeKey, f),
    checkIfFollowed,
  };
}
